import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { fromEvent, merge } from 'rxjs';
import { bufferTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { SessionService } from '@core/session/store/session.service';
import { SessionQuery } from '@core/session/store/session.query';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { environment } from '../environments/environment';
import { UserHubService } from '@core/session/hub/user-hub.service';
import { ConnectivityService } from '@core/health-check/connectivity.service';
import { GlobalAppSettingsService } from '@features/global/global-app-settings.service';
import { IcuDay } from '@features/process/intensive-care/icu-day';
import { FeatureTogglesService } from '@backend/api/feature-toggles/feature-toggles.service';
import { LicensingService } from '@backend/api/licensing/licensing.service';

@Component({
  selector: 'rais-app-root',
  template: `
    <router-outlet></router-outlet>
    <rais-kiosk-idle-screen />
  `,
  styles: [
    `
      :host {
        height: 100vh;
        width: 100vw;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit {
  // Инжектим, чтобы root-сервис создался на бутстрапе и начал следить за связью.
  private connectivity = inject(ConnectivityService);
  private globalAppSettings = inject(GlobalAppSettingsService);
  private featureToggles = inject(FeatureTogglesService);
  private licensing = inject(LicensingService);
  constructor(
    private sessionService: SessionService,
    private sessionQuery: SessionQuery,
    private userHub: UserHubService,
    private nzNotificationService: NzNotificationService,
  ) {
    // this.setupIdleHooks();

    this.setupDevEnvironmentPatches();

    // Эндпоинты feature toggle и глобальных настроек требуют сессию, поэтому грузим не на бутстрапе
    // (до логина = 401), а когда пользователь аутентифицирован. Перезагружаем при каждом входе.
    this.sessionQuery.user$
      .pipe(
        map((user) => !!user),
        distinctUntilChanged(),
      )
      .subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this.featureToggles.load();
          this.licensing.load();
          this.globalAppSettings.load().subscribe({
            next: (settings) => IcuDay.setStartOfDayOffset(settings.icu.icuDayStartOffset),
          });
        }
      });
  }

  setupIdleHooks() {
    merge(fromEvent(document, 'mousemove'), fromEvent(document, 'keypress'))
      .pipe(
        bufferTime(5 * 60 * 1000), //  5 минут
        filter(() => this.sessionQuery.isLoggedIn === true && this.sessionQuery.timeoutEnabled),
        filter((o) => o.length === 0),
      )
      .subscribe((o) => {
        this.sessionService.openLoginModal('timeout');

        this.nzNotificationService.info('Выход из системы', 'Выход из системы по причине неактивности пользователя');
      });
  }

  setupUserUpdateSubscription() {
    this.sessionQuery.user$.subscribe((user) =>
      this.userHub.subscribe({
        id: user?.id ?? 0,
      }),
    );
    // this.userHub.updated.subscribe((x) => this.sessionService.upsert(x));
  }

  ngOnInit() {
    this.userHub
      .connect()
      .then(() => this.setupUserUpdateSubscription())
      .catch((err) => console.log('Failed to connect to user hub: ', err));
  }

  setupDevEnvironmentPatches() {
    window['patchEnvironment'] = (val: any) => {
      environment.settings = {
        ...environment.settings,
        ...val,
      };

      return environment.settings;
    };

    window['getEnvironment'] = () => {
      return environment.settings;
    };
  }
}
